/**
 * The Leadership view (Phase 5A, Andres 2026-09-24): one page for firm leadership, who are usually also the
 * Board. "Our firm" and "The network", light on text and good on visuals, with quarterly trends and a
 * one-page PDF for meetings. Informative, never a verdict: no red, every number says what it measures.
 */
import {
  areaName,
  BUSINESS_RISK_CONSEQUENCE,
  findingCounts,
  findingsInOrder,
  headlineMeasures,
  medianDirection,
  quarterDate,
  surveyChange,
  surveyChangePhrase,
  findingsLine,
  impactText,
  unsortedNote,
  CDW_SCOPE,
  familyNote,
  UNSORTED_LABEL,
  openByBusinessRisk,
  type LeadershipAcceptance,
  type LeadershipFinding,
  BUSINESS_RISKS,
  LEADERSHIP_CONTENT_STATUS,
  apartSourceText,
  isApartDot,
  isHandsOn,
  rankedDots,
  partlyCountedFrom,
  PROGRAM_STEP_LABEL,
  PROGRAM_STEP_PLAIN,
  plainNextStep,
  PROGRAM_STEPS,
  programSummaryLine,
  servicesForControl,
  stepCounts,
  // 5D (DEC-49): the structured cards, focus points and sources, shared with the PDF.
  btiCards,
  firmSourceText,
  focusEmptyReason,
  focusShortReason,
  focusPoints,
  networkCards,
  networkSourceText,
  programCards,
  showsNetworkTrend,
  SOURCE,
  summaryCards,
  tileSource,
  typicalFirmNote,
  weakestCard,
  ratedChips,
  higherState,
  GOAL_SIZE,
  stateOf,
  programState,
  PROGRAM_STATE_CHIP,
  type ProgramState,
  ACCEPTANCE_UNLINKED_LABEL,
  weakestRisks,
  joinNames,
  type AnswerCard,
  type FocusPoint,
  type HeadlineMeasure,
  type StateChip,
  type CdwStart,
  type Coverage,
  type LeadershipData,
  type LeadershipFirm,
  type LeadershipFirmStanding,
  type LeadershipNetwork,
  type LeadershipProgramSummary,
  type ProgramStep,
  type ProgramStepStatus,
} from '@grc/shared/vocabulary';
// 5E (DEC-58): the program's name and the cycle's year, the one constant the PDF uses too.
import { ASSESSMENT_CYCLE_YEAR, PROGRAM_NAME } from '@grc/shared/leadership-measures';
import { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useApi, useDownloadState } from '../api';
import { Bullet, fullyText, InPlaceKey, inPlaceSummary, RankedBars, RankedColumns, Ring } from '../charts';
import { AnswerSection, formatDate, Load, PageBack, PageHeader, SourceLine } from '../components';
import { showDraftLabel } from '../config';
import { useWide } from '../Explained';
import { useMeContext } from '../Layout';
import { useViewParam, ViewTabs } from '../tabs';
import { AreasByFirm, BaselineByFirm, BTC_CHART, CriticalByFirm, FirmByFirmTable } from './FirmComparison';
import { FormerFirmPage } from './FormerFirm';
import { BtiWaves } from './LeadershipBti';
import { MILESTONE_LABEL, milestoneText, STAGE, type ProgramMilestone } from './Program';

const stageLabel = (s: string) => STAGE[s as keyof typeof STAGE]?.label ?? s;
const stageTone = (s: string) => STAGE[s as keyof typeof STAGE]?.tone ?? 'neutral';

const pct = (v: number | null | undefined) => (v === null || v === undefined ? 'No results yet' : `${Math.round(v)}%`);

const COVERAGE: Record<Coverage, { label: string; tone: string }> = {
  in_place: { label: 'Fully in place', tone: 'in_place' },
  partly: { label: 'Partly in place', tone: 'progress' },
  not_yet: { label: 'Not yet', tone: 'neutral' },
  not_rated: { label: 'Not rated', tone: 'neutral' },
};

/** The change since the March 2025 survey, both changes in the shared words (surveyChangePhrase, DEC-29, DEC-45), as a sentence start; null when none. */
const changeSince = (now: number | null, then: number | null, fully?: number | null): string | null => {
  if (now === null || then === null) return null;
  const c = surveyChange({ inPlace: now, fully }, then);
  if (!c) return null;
  const said = surveyChangePhrase(c, 'the March 2025 survey');
  return `${said[0]!.toUpperCase()}${said.slice(1)}`;
};

/** The same, spoken in full: "x of y in place; network median z%". */
const saidInPlace = (inPlace: number, total: number, median: number | null | undefined) =>
  median === null || median === undefined ? `${inPlace} of ${total} in place` : `${inPlace} of ${total} in place; network median ${pct(median)}`;

/** What two tiles measure, said under their labels. */
const CRITICAL_HINT = 'Against common attacks';
const RISKS_HINT = 'No agreed response yet';

type Dot = LeadershipNetwork['distribution'][number];

/** A member's own non-CDW dot is said apart (DEC-30); the rule is shared with the PDF (isApartDot, rankedDots). */
const apart = (d: Dot) => isApartDot(d);

/** The firms with CDW results, the only ones counted and ranked. */
const cdwDots = (n: LeadershipNetwork) => rankedDots(n.distribution).ranked;

/**
 * The firm's program as one figure (NEXT PASS 2, F1): the milestones done and the one under way, in plain step names.
 * The stage is the CDW tracker's own figure and is said in the card's chip, so the two never read as one count.
 */
const programLine = (f: LeadershipFirm): string => {
  const p = f.program;
  if (!p) return f.inCdwProgramme === false ? `${f.firmName} is not in ${PROGRAM_NAME}.` : `${f.firmName}: the CDW assessment is not scheduled yet.`;
  if (!p.steps || p.steps.length === 0) return `${f.firmName}: ${stageLabel(p.stage).toLowerCase().replace('cdw', 'CDW')}.`;
  const c = stepCounts(p.steps);
  // 5E (DEC-57): the step under way is the chip beside this answer, so the answer does not say it again.
  return `${c.done} of ${p.steps.length} milestones done`;
};

/** False when nothing about the firm's exposure is measured yet: no business risk coverage and no insurance rating. */
const measured = (f: LeadershipFirm): boolean => f.businessRisks.some((r) => r.coveragePct !== null) || f.insurance.some((i) => i.status !== 'not_rated');

/** True when the program's next step only repeats the milestone the stepper already shows under way. */
const repeatsMilestone = (p: LeadershipFirm['program']): boolean => {
  if (!p?.nextStep || !p.steps) return false;
  const norm = (t: string) => t.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const now = p.steps.find((s) => s.status === 'in_progress');
  return Boolean(now && norm(p.nextStep).startsWith(norm(PROGRAM_STEP_LABEL[now.step])));
};

/** Whether the network median moved since the previous quarter counted the same way; null when there is nothing to compare. */
/** The network trend's change: the one like-for-like direction (medianDirection), said as a sentence start. */
const networkMove = (n: LeadershipNetwork, asOf: string): string | null => {
  const m = medianDirection(n, asOf);
  return m ? `${m[0]!.toUpperCase()}${m.slice(1)}` : null;
};

/** A March 2025 survey tile's line, with what the survey did not cover (DEC-26). */
const surveyLine = (b: { inPlace: number; surveyed: number; notCovered: number }, spoken = false) =>
  `${spoken ? 'March 2025 survey' : 'Survey'}: ${b.inPlace} of ${b.surveyed} in place${b.notCovered ? `; ${b.notCovered} not covered${spoken ? ' by the survey' : ''}` : ''}`;

/**
 * A headline tile: a ring (fully solid, partly hatched; survey figures hatched grey) or a number, and one line that
 * says what it counts and how it compares (DEC-20). The colours are explained once per tab, not per tile.
 */
function Tile({ label, hint, pct: p, partly, median, text, value, line, said, hatched, tone, goal, before, source }: {
  label: string;
  /** 5D (F2): where the figure comes from, in a few words (tileSource). */
  source: string;
  /** 5C: the measure's goal, and its earlier value (or that there is none), under the line (DEC-37: "Earlier"). */
  goal?: string;
  before?: string;
  /** What the measure is, in a few words, under the label. */
  hint?: string;
  pct?: number | null;
  partly?: number | null;
  /** The network median, drawn as a tick on the ring (the key names it). */
  median?: number | null;
  text?: string;
  value?: string;
  /** Under a heading that already says it ("The typical firm"), a tile shows no line; screen readers still hear `said`. */
  line?: string;
  /** What a screen reader hears in place of the short line, when the line leans on the key. */
  said?: string;
  hatched?: boolean;
  /** "count" for figures that are not "in place" (plans, firms): the ring is not drawn in the teal of fully in place. */
  tone?: 'count';
}) {
  const ring = p !== undefined;
  return (
    <section className="card lead-big lead-tile" aria-label={label}>
      <span className="lead-big-label">
        {label}
        {hint && <span className="lead-tile-hint">{hint}</span>}
      </span>
      {ring ? (
        <Ring
          // 84 with tight labels (NEXT PASS 5; 5E, after the larger questions of DEC-58): "N% fully" stays inside the arc,
          // and the four tiles still fit the first 1280 x 800 screen (5C L2). The label fonts are set in app.css.
          size={84}
          tight
          pct={p ?? null}
          partly={partly ?? null}
          hatched={hatched}
          tone={tone}
          median={median ?? null}
          text={text ?? (p === null ? 'None' : undefined)}
          sub={!hatched && p !== null && partly ? fullyText(p!, partly) : undefined}
          label={`${label}${hint ? ` (${hint.toLowerCase()})` : ''}: ${text ?? (p === null ? 'no results yet' : pct(p))}. ${said ?? line ?? ''}${!hatched && p !== null && partly ? `. ${inPlaceSummary(p!, partly)}` : ''}`}
        />
      ) : (
        <span className="lead-big-value">{value}</span>
      )}
      {/* 5E NEXT PASS 5 (E1): in the six-tile grid every tile keeps the line's row, so Goal and Earlier line up. */}
      {line ? <span className="lead-tile-line">{line}</span> : <span className="lead-tile-spacer" aria-hidden="true" />}
      {(goal || before) && (
        <dl className="lead-goal small">
          {goal && (
            <div>
              <dt>Goal</dt>
              <dd>{goal}</dd>
            </div>
          )}
          {before && (
            <div>
              <dt>Earlier</dt>
              {/* F1 (NEXT PASS 1): one part per line (the earlier figure, then each change); the text is unchanged. */}
              <dd className="lead-earlier">
                {before.split('; ').map((part, i, all) => (i < all.length - 1 ? [`${part}; `, <br key={i} />] : part))}
              </dd>
            </div>
          )}
        </dl>
      )}
      <p className="lead-source lead-tile-source">Source: {source}</p>
    </section>
  );
}

/**
 * Quarterly trend: the firm against the network median, as two lines. Snapshots before 2026-09-26 counted
 * fully in place only, so where a trend spans both rules the quarter it changed is marked and said in words,
 * instead of letting the step read as progress.
 */
function Trend({ points, caption, aIsMedian, wide = false }: { points: Array<{ quarter: string; a: number | null; b: number | null; countsPartly: boolean }>; caption: string; /** Drawn across a full-width card (5E NEXT PASS 2): a wider chart area, so it fills the card at its height. */ wide?: boolean; /** The main line is itself the network median (the network trend). */ aIsMedian?: boolean }) {
  // Hooks first, before any early return.
  const roomy = useWide(600);
  const shown = points.filter((p) => p.a !== null || p.b !== null);
  if (shown.length < 2) return <p className="muted small">The trend starts once two quarters of figures are in.</p>;
  const changeFrom = partlyCountedFrom(shown);
  const changeAt = changeFrom ? shown.findIndex((p) => p.quarter === changeFrom) : -1;
  // Left margin for the 0 to 100% scale; end labels anchored inward so none is clipped (5A C6, 2026-10-03).
  // 5E NEXT PASS 3 (E1): the wide drawing only where there is room for it; on a phone (under 600 px) the narrow one,
  // so the scale and quarter labels stay readable instead of shrinking with a 760-unit drawing.
  const W = wide && roomy ? 760 : 320;
  const H = wide && roomy ? 130 : 120;
  // 38: the scale's labels keep clear of the edge when the chart is drawn wider (5E NEXT PASS 1, half-width cards).
  const L = 38;
  const R = 26;
  const x = (i: number) => L + (i * (W - L - R)) / (shown.length - 1);
  const y = (v: number) => H - 20 - (v / 100) * (H - 32);
  const last = shown[shown.length - 1]!;
  // Where the counting rule changed, the line breaks: the step is a new rule, not progress (5A C6, 2026-10-03).
  const spans: Array<[number, number]> = changeAt > 0 ? [[0, changeAt - 1], [changeAt, shown.length - 1]] : [[0, shown.length - 1]];
  const lines = (key: 'a' | 'b') =>
    spans.map(([from, to]) =>
      shown
        .slice(from, to + 1)
        .map((p, j) => (p[key] === null ? null : `${x(from + j)},${y(p[key]!)}`))
        .filter(Boolean)
        .join(' '),
    );
  // Quarters are said as dates, from the one shared formatter (quarterDate): a full date in words, a short one on the axis.
  const changeNote = changeFrom ? `From ${quarterDate(changeFrom)}, partly in place counts as in place. Earlier quarters count fully in place only.` : '';
  return (
    <figure className="lead-trend">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`${caption}. ${shown.map((p) => `${quarterDate(p.quarter)}: ${pct(p.a)}${p.b === null ? '' : ` (network median ${pct(p.b)})`}`).join('; ')}${changeNote ? `. ${changeNote}` : ''}`}>
        {[0, 50, 100].map((v) => (
          <g key={v}>
            <line x1={L} x2={W - R} y1={y(v)} y2={y(v)} className={v === 0 ? "lead-axis" : "lead-gridline"} />
            <text x={L - 6} y={y(v) + 3} textAnchor="end" className="lead-tick">
              {v}%
            </text>
          </g>
        ))}
        {changeAt > 0 && <line x1={(x(changeAt - 1) + x(changeAt)) / 2} x2={(x(changeAt - 1) + x(changeAt)) / 2} y1={8} y2={y(0)} className="lead-change-line" />}
        {lines('b').map((pts, k) => (
          <polyline key={`b${k}`} points={pts} className="lead-line-median" />
        ))}
        {lines('a').map((pts, k) => (
          <polyline key={`a${k}`} points={pts} className={aIsMedian ? 'lead-line-median' : 'lead-line-firm'} />
        ))}
        {shown.map((p, i) => (
          <g key={p.quarter}>
            {p.b !== null && <circle cx={x(i)} cy={y(p.b)} r="3" className="lead-dot-median" />}
            {p.a !== null && <circle cx={x(i)} cy={y(p.a)} r="4" className={aIsMedian ? 'lead-dot-median' : 'lead-dot-firm'} />}
            <text x={x(i)} y={H - 4} textAnchor={i === 0 ? "start" : i === shown.length - 1 ? "end" : "middle"} className="lead-tick">
              {quarterDate(p.quarter, 'short')}
            </text>
          </g>
        ))}
        {last.a !== null && (
          <text x={x(shown.length - 1) - 6} y={y(last.a) - 8} textAnchor="end" className="lead-value">
            {pct(last.a)}
          </text>
        )}
      </svg>
      <figcaption className="legend small">
        <span className={aIsMedian ? 'key-median-line' : 'key-firm key-count'} aria-hidden="true" /> {caption.split(':')[0]}
        {shown.some((p) => p.b !== null) && (
          <>
            {' '}
            <span className="key-median-line" aria-hidden="true" /> Network median
          </>
        )}
        {changeFrom && <span className="muted trend-note"> Vertical line: from {quarterDate(changeFrom)}, partly in place counts too.</span>}
      </figcaption>
    </figure>
  );
}

/** The plan of next steps, the sixth step: the session that turns the report into action plans. */
const roadmapText = (s: ProgramStep) =>
  s.status === 'complete' ? 'Done' : s.status === 'in_progress' ? (s.dateText ? `Under way, ${s.dateText}` : 'Under way') : s.status === 'not_started' ? 'Not started' : 'To be confirmed';

/** The milestones' state in a few words, so the chip describes the answer (NEXT PASS 2, F1). */
/** 5E (DEC-57): the chip names the step, so it says a state the answer's count does not ("Risk report under way"). */
const milestoneChip = (steps: readonly ProgramStep[]): StateChip => {
  const c = stepCounts(steps);
  // PL-15 (5E pre-gate): a finished assessment says so; the next step is said as next.
  if (c.done === steps.length) return { label: 'Assessment complete', tone: 'neutral' };
  const now = steps.find((x) => x.status === 'in_progress');
  if (now) return { label: `${PROGRAM_STEP_PLAIN[now.step]} under way`, tone: 'neutral' };
  const next = steps.find((x) => x.status !== 'complete');
  return { label: c.done === 0 || !next ? 'Not started yet' : `Next: ${PROGRAM_STEP_PLAIN[next.step].toLowerCase()}`, tone: 'neutral' };
};

/** Where the firm is in the CDW program, and when it was last assessed, independently and by itself. */
function FirmProgram({ f }: { f: LeadershipFirm }) {
  const p = f.program;
  const a = f.assessments;
  // NEXT PASS 2 (F1): the structured form, with one figure. The answer and the chip both speak of the milestones; the
  // tracker's own stage word ("Assessment complete") is said once, in the meaning, as CDW's word, so it never sits beside
  // "5 of 6 milestones done" as a second figure.
  const steps = Boolean(p && p.steps && p.steps.length > 0);
  return (
    <Section
      id="fprog-h"
      className="lead-program-card"
      question={`Where is ${f.firmName} in ${PROGRAM_NAME}?`}
      answer={<span className="lead-program-line">{programLine(f)}</span>}
      chip={p && p.steps && p.steps.length > 0 ? milestoneChip(p.steps) : PROGRAM_STATE_CHIP[programState(f)](null)}
      meaning={
        p && steps
          ? `A milestone is one of the six steps of the CDW assessment, from the start meeting to the plan of next steps. CDW's own tracker records this assessment as "${stageLabel(p.stage)}".`
          : `Each firm in ${PROGRAM_NAME} has a break-in test, a risk report and a plan of next steps.`
      }
      source={SOURCE.tracker}
    >
      {p && p.steps && p.steps.length > 0 ? (
        <>
          <ol className="plain stepper six" aria-label="Program milestones">
            {p.steps.map((s) => (
              <li key={s.step} className={`step ms-${s.status}`}>
                <span className="step-dot" aria-hidden="true" />
                <span className="step-name">{PROGRAM_STEP_PLAIN[s.step]}</span>
                <span className="step-when">{s.step === 'roadmap' ? roadmapText(s) : milestoneText({ milestone: s.step as ProgramMilestone['milestone'], status: s.status, date: s.date, dateText: s.dateText, dateKind: null })}</span>
              </li>
            ))}
          </ol>
          <MilestoneNames />
        </>
      ) : (
        p &&
        p.milestones.length > 0 && (
          <ol className="plain stepper" aria-label="Program milestones">
            {p.milestones.map((m) => {
              const ms = { ...m, dateKind: null } as ProgramMilestone;
              return (
                <li key={m.milestone} className={`step ms-${m.status}`}>
                  <span className="step-dot" aria-hidden="true" />
                  <span className="step-name">{MILESTONE_LABEL[ms.milestone] ?? m.milestone}</span>
                  <span className="step-when">{milestoneText(ms)}</span>
                </li>
              );
            })}
          </ol>
        )
      )}
      <dl className="lead-facts">
        <div>
          <dt>Last independent assessment</dt>
          <dd className="lead-fact">{a.lastIndependent ? (a.lastIndependent.date ? formatDate(a.lastIndependent.date) : 'CDW 2026') : 'Not yet'}</dd>
          <dd className="muted small">{a.lastIndependent ? a.lastIndependent.label : f.inCdwProgramme === false ? 'No CDW assessment planned' : 'Results arrive with the CDW report'}</dd>
        </div>
        <div>
          <dt>Last self-assessment update</dt>
          <dd className="lead-fact">{a.lastSelfAssessment ? formatDate(a.lastSelfAssessment) : 'None yet'}</dd>
          <dd className="muted small">{a.selfAssessedControls === 0 ? 'No controls rated yet' : a.selfAssessedControls === 1 ? 'One control rated by the firm' : `${a.selfAssessedControls} controls rated by the firm`}</dd>
        </div>
        {p?.cdwStart && (
          <div>
            <dt>CDW assessment started</dt>
            <dd className="lead-fact">{cdwStartText(p.cdwStart)}</dd>
            <dd className="muted small">First dated assessment activity in 2026</dd>
          </div>
        )}
        {!repeatsMilestone(p) && (
        <div>
          <dt>Next step</dt>
          <dd>{plainNextStep(p?.nextStep ?? null) ?? (f.inCdwProgramme === false ? `Talk to National IT about joining ${PROGRAM_NAME}` : 'National schedules the assessment with the firm')}</dd>
        </div>
        )}
      </dl>
      {/* NEXT PASS 2 (F2): the self-assessment facts come from the firm's own ratings, not the tracker. */}
      <SourceLine source={`Last self-assessment update: ${f.firmName}'s own ratings in this portal`} />
    </Section>
  );
}
/* ---------------------------------------------------------------------------------------------------------------
 * 5C, Leadership for the Board (PLAN 2026-10-05, criteria L1 to L10; DEC-36, DEC-37): the Summary and the
 * Assessment program. Findings, risk acceptances and the network's business risks come from the API (findings.ts);
 * the opening lines come from leadership-lines.ts, the same lines the one-page PDF prints.
 * ------------------------------------------------------------------------------------------------------------- */

// 5D (DEC-49): no "open for" age on the Board's lists any more; firms manage owners, dates and age on their own pages.
const riskLabel = (id: string | null) => (id ? (BUSINESS_RISKS.find((r) => r.id === id)?.label ?? id) : 'Outside the four business risks');
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** A state chip (F1): informative words; the off-track tone only for a committed plan off track. */
/**
 * A view's opening (5D, F1): up to three answer cards, each a small question, the answer as a figure or short
 * statement, a state chip, the shared opening line (DEC-37, word for word, the same the PDF prints) and its source.
 */
/**
 * `note`: said after the main lines, never leading them (L1), inside the last card so it sits in a part with its
 * source (NEXT PASS 2, F2): the open findings not linked to a business risk yet.
 */
function OpeningCards({ cards, note }: { cards: AnswerCard[]; note?: string | null }) {
  if (!cards.length) return null;
  return (
    <div className="lead-opening">
      {/* 5E (DEC-58): the shared AnswerSection, so these questions read exactly as every other question on the page. */}
      {cards.map((c, i) => (
        <AnswerSection
          key={c.id}
          look="opening"
          id={c.id}
          question={c.question}
          answer={c.answer}
          chip={c.chip}
          meaning={c.line}
          note={note && i === cards.length - 1 ? <p className="muted small lead-unsorted">{note}</p> : undefined}
          source={c.source}
        />
      ))}
    </div>
  );
}

/** A structured section (5D, F1): the shared AnswerSection in its section look (UX-09). */
const Section = (p: Omit<Parameters<typeof AnswerSection>[0], 'look'> & { source: string }) => <AnswerSection {...p} />;

/** The four business risks with how much protection is in place, each with what it would mean (Summary and Assessment program). */
function BusinessRiskRows({ rows, scope, unlinked = 0 }: { rows: Array<{ id: string; label: string; pct: number | null; partly: number | null; open?: number; firmsWith?: number }>; scope: string; /** Open findings not linked to the baseline controls: their risk is not known, so none is ruled out. */ unlinked?: number }) {
  return (
    <ul className="plain lead-brisks">
      {rows.map((r) => (
        <li key={r.id}>
          <div className="lead-brisk-text">
            <strong>{r.label}</strong>
            <span className="small">{BUSINESS_RISK_CONSEQUENCE[r.id]}</span>
          </div>
          <Bullet pct={r.pct} partly={r.partly} label={`${r.label}: ${pct(r.pct)} of the protections in place${scope}${r.pct !== null && r.partly !== null ? `. ${inPlaceSummary(r.pct, r.partly)}` : ''}`} />
          <span className="small lead-brisk-value">
            {r.pct === null ? 'Not measured yet' : `${pct(r.pct)} in place`}
            {r.pct !== null && r.partly !== null && <span className="bar-fully"> {fullyText(r.pct, r.partly)}</span>}
            {r.open !== undefined && (
              <span className="lead-brisk-open">
                {r.open === 0 ? (unlinked ? 'No open finding sorted under it yet' : 'No open findings') : `${plural(r.open, 'open finding')}${r.firmsWith ? ` at ${plural(r.firmsWith, 'firm')}` : ''}`}
              </span>
            )}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** "See the detail": the committee and IT layer, one click down (PLAN item 6); its name says what opens (CP-03). */
function Detail({ what, children }: { what: string; children: React.ReactNode }) {
  return (
    <details className="lead-detail">
      <summary>See the detail: {what}</summary>
      <div className="lead-detail-body">{children}</div>
    </details>
  );
}

/**
 * The least protected business risk as a section's answer (5D, F1): the same rule as the opening line (weakestRisks),
 * with the meaning line in the form Andres set: what share of the protections is in place, across which firms.
 */
function riskAnswer(rows: Array<{ label: string; pct: number | null }>, across: string): { answer: string; chip: StateChip; meaning: string } {
  // The answer and chip are the Summary card's own (weakestCard, UX-09); the meaning line says across which firms.
  const { answer, chip } = weakestCard(rows);
  const w = weakestRisks(rows);
  if (!w) return { answer, chip, meaning: 'Each bar shows how much of the protection against one business risk is in place, once there are results.' };
  if (w.pct >= 100) return { answer, chip, meaning: `Every protection against the four business risks is in place, fully or partly, ${across}.` };
  const names = joinNames(w.rows.map((r) => r.label.toLowerCase()));
  return { answer, chip, meaning: `${w.pct}% of the protections against ${w.rows.length === 1 ? names : `each of ${names}`} are in place ${across}; each bar below is one business risk.` };
}

/**
 * Where member firms can focus (5D, F3; DEC-49 replaces "Decisions waiting"): three to five network-wide points
 * generated from the data the reader may see (focusPoints), the same the PDF prints. Each says its rule's source.
 */
/** What the focus points are drawn from, naming only the rules that produced one (PL-23). */
const FOCUS_RULE_WORDS: Record<FocusPoint['rule'], string> = {
  wave2: 'what is due next',
  common_gap: 'the gaps the most firms share',
  weakest_risk: 'the least protected business risk',
  to_strengthen: 'the control the most firms have only partly in place',
  program: 'the assessments still to schedule',
};
const focusMeaning = (d: LeadershipData, points: FocusPoint[]): string => {
  const parts = [...new Set(points.map((p) => FOCUS_RULE_WORDS[p.rule]))];
  const short = focusShortReason(d, points);
  return `${parts.length ? `Generated from today's figures: ${joinNames(parts)}.` : "Generated from today's figures."}${short ? ` ${short}` : ''}`;
};

function FocusSection({ d }: { d: LeadershipData }) {
  const points = focusPoints(d);
  const sources = [...new Set(points.flatMap((p) => p.source.split('; ')))];
  return (
    <Section
      id="focus-h"
      className="lead-focus-card"
      question="Where can member firms focus to improve the network's security?"
      answer={points.length ? `${plural(points.length, 'point')} across the network` : 'Nothing stands out yet'}
      // F1: the chip describes the card's own answer (where the points apply), not Wave 2, which is the first point.
      // NEXT PASS 2: the chip says what the answer is drawn from.
      // 5E (DEC-57): the chip says a state and never repeats the answer's count: the points come from today's figures,
      // fewer than three is said as such (DEC-56: never padded), and with none the chip says what it waits for.
      // PL-13 (5E pre-gate): the date the points were worked out on.
      chip={{ label: points.length >= 3 ? `As of ${formatDate(d.asOf)}` : points.length ? 'Fewer than three stand out' : 'Waiting for more results', tone: 'neutral' }}
      meaning={focusMeaning(d, points)}
      source={sources.join('; ') || SOURCE.cdw}
    >
      {points.length ? (
        <ul className="plain lead-focus" aria-label="Where member firms can focus">
          {points.map((p) => (
            <li key={`${p.rule}-${p.lead}`} data-rule={p.rule}>
              <strong>{p.lead}.</strong> {p.text}
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted small">{focusEmptyReason(d.network)}</p>
      )}
    </Section>
  );
}

/** The business risks as a structured section (5D, F1), for the network (the typical firm) or one firm. */
function RiskSection({ id, rows, across, source, scope, unlinked, children }: {
  id: string;
  rows: Array<{ id: string; label: string; pct: number | null; partly: number | null; open?: number; firmsWith?: number }>;
  /** Where the figures are measured, for the meaning line: "at the typical firm, across the 7 firms with CDW results". */
  across: string;
  source: string;
  /** For the bars' spoken labels. */
  scope: string;
  unlinked?: number;
  children?: React.ReactNode;
}) {
  // NEXT PASS 2 (F1 item 6): on the Summary the opening card "What needs attention?" already names the least protected
  // risk, so this section answers a different question: how far protection runs across the four risks.
  const onSummary = id === 'sum-risk-h';
  const a = riskAnswer(rows, across);
  const v = rows.map((r) => r.pct).filter((x): x is number => x !== null);
  const spread = v.length ? (Math.min(...v) === Math.max(...v) ? pct(v[0]!) : `${pct(Math.min(...v))} to ${pct(Math.max(...v))}`) : 'Not measured yet';
  return (
    <Section
      id={id}
      question={onSummary ? 'How much protection is in place against each business risk?' : 'Which business risk is least protected?'}
      answer={onSummary ? spread : a.answer}
      // 5E (DEC-57): a state that does not repeat the range in the answer: which risk is least protected.
      chip={onSummary ? (v.length ? { label: `Least protected: ${leastLabel(rows)}`, tone: 'neutral' } : { label: 'No results yet', tone: 'neutral' }) : a.chip}
      meaning={onSummary ? `Each bar is the share of the protections against one business risk in place, fully or partly, ${across}.` : a.meaning}
      source={source}
    >
      <BusinessRiskRows rows={rows} scope={scope} unlinked={unlinked} />
      {children}
      {showDraftLabel(LEADERSHIP_CONTENT_STATUS) && <p className="muted small">Draft: the business risk groupings are National guidance under review.</p>}
    </Section>
  );
}

/** The least protected business risk by name, from the one rule the opening line uses (weakestRisks). */
const leastLabel = (rows: Array<{ label: string; pct: number | null }>): string => {
  const w = weakestRisks(rows);
  return w ? joinNames(w.rows.map((r) => r.label.toLowerCase())) : 'not measured yet';
};

/** "at the typical firm, across the 7 firms with CDW results": where a network figure is measured. */
const acrossNetwork = (n: LeadershipNetwork) => `at the typical firm, across the ${plural(n.firmsWithResults, 'firm')} with CDW results`;

/**
 * The network's quarterly chart (5D, F5, DEC-49): hidden until four quarters are stored (showsNetworkTrend), then shown
 * by itself, on the Summary's detail and on the network tab.
 */
function NetworkTrendCard({ id, n, asOf, wide = false }: { id: string; n: LeadershipNetwork; asOf: string; /** Full width, on the network tab; otherwise it shares a row (the Summary's detail). */ wide?: boolean }) {
  if (!showsNetworkTrend(n)) return null;
  return (
    // 5E NEXT PASS 1/2 (E1): in a row of three it is one card among equals; full width, the chart is drawn wider, so
    // either way it fills its card.
    <Section
      half={!wide}
      id={id}
      name="Network by quarter"
      question="How has the typical firm moved, quarter by quarter?"
      answer={n.btcMedian === null ? 'Not measured yet' : `${pct(n.btcMedian)} of the BTC Baseline now`}
      chip={{ label: networkMove(n, asOf) ?? 'No earlier quarter to compare', tone: 'neutral' }}
      meaning="Each point is the typical firm's share of the BTC Baseline in place at the end of a quarter, as stored by the portal."
      source={`${SOURCE.snapshots}; ${SOURCE.cdw}`}
    >
      <Trend wide={wide} caption="BTC Baseline median: by quarter" aIsMedian points={n.trend.map((t) => ({ quarter: t.quarter, a: t.btcMedian, b: null, countsPartly: t.countsPartly }))} />
    </Section>
  );
}

/**
 * Summary, for the Board and for a picked firm (5D): the answer cards, the four headline measures against their goals,
 * the least protected business risk, the other measures one click down, and where member firms can focus.
 */
function Summary({ d }: { d: LeadershipData }) {
  const f = d.firm;
  const n = d.network as LeadershipNetwork;
  const fm = n.fullyMedians;
  const asOf = d.asOf;
  const src = (k: HeadlineMeasure) => tileSource(k, d);
  if (!f) {
    const risks = (n.businessRisks ?? []).map((r) => ({ id: r.id, label: r.label, pct: r.coveragePct, partly: partlyOf(r.coveragePct, r.fullyPct) }));
    const m = headlineMeasures(d);
    return (
      <section aria-labelledby="network-h" className="lead-band">
        <h2 id="network-h" className="sr-only">The network</h2>
        <OpeningCards cards={summaryCards(d)} />
        <div className="lead-grid">
        <Section
          id="sum-tiles-h"
          className="lead-tiles-section"
          foot
          question="Where does the typical firm stand on each measure?"
          answer={n.btcMedian === null ? 'Not measured yet' : `${pct(n.btcMedian)} of the BTC Baseline`}
          chip={stateOf(n.btcMedian)}
          meaning="Each tile is one headline measure at the typical firm, against its goal, with its earlier figure."
          source={networkSourceText(n)}
        >
        <div className="lead-bigs four">
          <Tile label="BTC Baseline" pct={n.btcMedian} partly={partlyOf(n.btcMedian, fm?.btc)} said={TYPICAL} goal={m.btc.goal} before={m.btc.earlier} source={src('btc')} />
          <Tile label="BTI Wave 2 (2026)" pct={n.btiWave2Median} partly={partlyOf(n.btiWave2Median, fm?.btiWave2)} said={TYPICAL} goal={m.btiWave2.goal} before={m.btiWave2.earlier} source={src('btiWave2')} />
          <Tile label="Critical protections" hint={CRITICAL_HINT} pct={n.criticalMedianPct} partly={partlyOf(n.criticalMedianPct, fm?.critical)} said={TYPICAL} goal={m.critical.goal} before={m.critical.earlier} source={src('critical')} />
          <Tile label="Plans on track or complete" tone="count" pct={n.plansOnTrackPct} said={TYPICAL} goal={m.plans.goal} before={m.plans.earlier} source={src('plans')} />
        </div>
        </Section>
        </div>
        <div className="lead-grid">
          {risks.length > 0 && <RiskSection id="sum-risk-h" rows={risks} across={acrossNetwork(n)} source={networkSourceText(n)} scope=" at the typical firm" />}
        </div>
        <Detail what={showsNetworkTrend(n) ? "the network's trend and its other measures" : "the network's other measures"}>
          <div className="lead-grid">
            <NetworkTrendCard id="sum-ntrend-h" n={n} asOf={asOf} />
            {/* NEXT PASS 3 (F1): the measures the headline row leaves out, in the structured form. NEXT PASS 4: one
                question each, so each chip describes its own answer. */}
            <Section
              half
              id="sum-more-h"
              question="Where does the typical firm stand on BTI Wave 1?"
              answer={n.btiWave1Median === null ? 'Not measured yet' : `${pct(n.btiWave1Median)} of BTI Wave 1`}
              chip={stateOf(n.btiWave1Median)}
              meaning="BTI Wave 1 is the 2025 set of requirements; the figure is the typical firm's share of them in place."
              source={networkSourceText(n)}
            >
              <div className="lead-bigs">
                <Tile label="BTI Wave 1 (2025)" pct={n.btiWave1Median} partly={partlyOf(n.btiWave1Median, fm?.btiWave1)} said={TYPICAL} goal={m.btiWave1.goal} before={m.btiWave1.earlier} source={src('btiWave1')} />
              </div>
            </Section>
            <Section
              half
              id="sum-firms-h"
              question="How many member firms have CDW results?"
              answer={`${n.firmsWithResults} of ${n.firmsInNetwork} firms`}
              chip={n.firmsWithResults >= n.firmsInNetwork ? { label: 'Every firm has results', tone: 'in_place' } : { label: `${n.firmsInNetwork - n.firmsWithResults} waiting for CDW results`, tone: 'neutral' }}
              meaning="A firm counts here once its CDW assessment has reported; the network's figures are the medians of these firms."
              source={tileSource('firms', d)}
            >
              <div className="lead-bigs">
                <Tile label="Firms with results" tone="count" pct={n.firmsInNetwork ? (n.firmsWithResults / n.firmsInNetwork) * 100 : null} text={`${n.firmsWithResults}/${n.firmsInNetwork}`} line="With CDW results" goal={m.firms.goal} before={m.firms.earlier} source={src('firms')} />
              </div>
            </Section>
          </div>
        </Detail>
        <div className="lead-grid">
          <FocusSection d={d} />
        </div>
      </section>
    );
  }
  const h = f.headline;
  const s = f.source === null ? f.march2025Baselines ?? null : null;
  const m = headlineMeasures(d);
  const committed = h.plans.total - h.plans.notCommitted;
  const risks = f.businessRisks.map((r) => ({ id: r.id, label: r.label, pct: r.coveragePct, partly: r.coveragePct === null || !r.total ? null : (r.partly / r.total) * 100 }));
  return (
    <section aria-labelledby="our-firm-h" className="lead-band">
      <h2 id="our-firm-h" className="sr-only">{f.firmName}</h2>
      <OpeningCards cards={summaryCards(d)} />
      <div className="lead-grid">
      <Section
        id="sum-tiles-h"
        className="lead-tiles-section"
        foot
        question={`Where does ${f.firmName} stand on each measure?`}
        answer={h.btc.value === null ? (s ? 'March 2025 survey only' : 'No results yet') : `${pct(h.btc.value)} of the BTC Baseline`}
        chip={s ? { label: 'March 2025 survey', tone: 'neutral' } : ratedChips(h.btc.value, h.btc.total, GOAL_SIZE.btc).position}
        meaning={`Each tile is one headline measure for ${f.firmName}, against its goal and the typical firm, with its earlier figure.`}
        source={firmSourceText(f)}
      >
      <div className="lead-bigs four">
        {s ? (
          <>
            <Tile label="BTC Baseline" hatched pct={s.btc.pct} text={pct(s.btc.pct)} line={surveyLine(s.btc)} said={surveyLine(s.btc, true)} goal={m.btc.goal} before={m.btc.earlier} source={src('btc')} />
            <Tile label="BTI Wave 2 (2026)" hatched pct={s.btiWave2.pct} text={s.btiWave2.surveyed ? pct(s.btiWave2.pct) : 'None'} line={s.btiWave2.surveyed ? surveyLine(s.btiWave2) : 'Not in the March 2025 survey'} said={s.btiWave2.surveyed ? surveyLine(s.btiWave2, true) : undefined} goal={m.btiWave2.goal} before={m.btiWave2.earlier} source={src('btiWave2')} />
          </>
        ) : (
          <>
            <Tile
              label="BTC Baseline"
              pct={h.btc.value}
              partly={h.btc.total ? (h.btc.partly / h.btc.total) * 100 : null}
              median={h.btc.networkMedian}
              line={h.btc.value === null ? 'No results yet' : `${h.btc.inPlace} of ${h.btc.total} in place`}
              said={h.btc.value === null ? undefined : saidInPlace(h.btc.inPlace, h.btc.total, h.btc.networkMedian)}
              goal={m.btc.goal}
              before={m.btc.earlier}
              source={src('btc')}
            />
            <Tile
              label="BTI Wave 2 (2026)"
              pct={h.bti.wave2.value}
              partly={h.bti.wave2.total ? (h.bti.wave2.partly / h.bti.wave2.total) * 100 : null}
              median={h.bti.wave2.networkMedian}
              line={h.bti.wave2.value === null ? 'No results yet' : `${h.bti.wave2.inPlace} of ${h.bti.wave2.total} in place`}
              said={h.bti.wave2.value === null ? undefined : saidInPlace(h.bti.wave2.inPlace, h.bti.wave2.total, h.bti.wave2.networkMedian)}
              goal={m.btiWave2.goal}
              before={m.btiWave2.earlier}
              source={src('btiWave2')}
            />
          </>
        )}
        {h.critical.total === 0 ? (
          <Tile label="Critical protections" hint={CRITICAL_HINT} pct={null} text="None" line="Shown once rated" goal={m.critical.goal} before={m.critical.earlier} source={src('critical')} />
        ) : (
          <Tile
            label="Critical protections"
            hint={CRITICAL_HINT}
            pct={(h.critical.inPlace / h.critical.total) * 100}
            partly={(h.critical.partly / h.critical.total) * 100}
            median={h.critical.networkMedianPct}
            line={`${h.critical.inPlace} of ${h.critical.total} in place`}
            said={saidInPlace(h.critical.inPlace, h.critical.total, h.critical.networkMedianPct)}
            goal={m.critical.goal}
            before={m.critical.earlier}
            source={src('critical')}
          />
        )}
        {committed ? (
          <Tile
            label="Plans on track or complete"
            tone="count"
            pct={((h.plans.onTrack + h.plans.complete) / committed) * 100}
            text={`${h.plans.onTrack + h.plans.complete}/${committed}`}
            median={d.network.plansOnTrackPct}
            line={h.plans.notCommitted ? `${h.plans.notCommitted} more not committed yet` : 'Of committed plans'}
            said={`${h.plans.onTrack + h.plans.complete} of ${committed} committed plans on track or complete${h.plans.notCommitted ? `; ${h.plans.notCommitted} not committed yet` : ''}`}
            goal={m.plans.goal}
            before={m.plans.earlier}
            source={src('plans')}
          />
        ) : (
          <Tile label="Plans on track or complete" value="None" line={h.plans.notCommitted ? `${h.plans.notCommitted} not committed yet` : 'After CDW assessment'} goal={m.plans.goal} before={m.plans.earlier} source={src('plans')} />
        )}
      </div>
      </Section>
      </div>
      {measured(f) && (
        <div className="lead-grid">
          <RiskSection id="sum-risk-h" rows={risks} across={`at ${f.firmName}`} source={firmSourceText(f)} scope={` at ${f.firmName}`} />
        </div>
      )}
      <Detail what={`${f.firmName}'s trend and its other measures`}>
        <div className="lead-grid">
          {/* NEXT PASS 2 (F1): the structured form; the change since March 2025 is the chip, in the shared words. */}
          <Section
            half
            id="ftrend-h"
            name="BTC Baseline by quarter"
            question={`How has ${f.firmName} moved, quarter by quarter?`}
            answer={h.btc.value === null ? 'No results yet' : `${pct(h.btc.value)} of the BTC Baseline now`}
            chip={{ label: changeSince(h.btc.value, h.btc.march2025, h.btc.total ? ((h.btc.inPlace - h.btc.partly) / h.btc.total) * 100 : null) ?? 'No earlier figure to compare', tone: 'neutral', title: h.btc.march2025 === null ? undefined : `March 2025 survey: ${Math.round(h.btc.march2025)}%` }}
            meaning={`Each point is ${f.firmName}'s share of the BTC Baseline in place at the end of a quarter, beside the typical firm's.`}
            source={`${SOURCE.snapshots}; ${firmSourceText(f)}`}
          >
            <Trend caption={`${f.firmName}: BTC Baseline by quarter`} points={f.trend.map((t) => ({ quarter: t.quarter, a: t.firm, b: t.networkMedian, countsPartly: t.countsPartly }))} />
          </Section>
          {/* NEXT PASS 3 (F1): the measures the headline row leaves out, in the structured form. */}
          <Section
            half
            id="sum-fmore-h"
            question={`Where does ${f.firmName} stand on BTI Wave 1?`}
            answer={s ? (s.btiWave1.surveyed ? `${pct(s.btiWave1.pct)} of BTI Wave 1` : 'Not in the March 2025 survey') : h.bti.wave1.value === null ? 'No results yet' : `${pct(h.bti.wave1.value)} of BTI Wave 1`}
            chip={s ? { label: 'March 2025 survey', tone: 'neutral' } : ratedChips(h.bti.wave1.value, h.bti.wave1.total, GOAL_SIZE.wave1).position}
            meaning={`BTI Wave 1 is the 2025 set of requirements; the figure is ${f.firmName}'s share of them in place.`}
            source={firmSourceText(f)}
          >
          <div className="lead-bigs">
            {s ? (
              <Tile label="BTI Wave 1 (2025)" hatched pct={s.btiWave1.pct} text={s.btiWave1.surveyed ? pct(s.btiWave1.pct) : 'None'} line={s.btiWave1.surveyed ? surveyLine(s.btiWave1) : 'Not in the March 2025 survey'} said={s.btiWave1.surveyed ? surveyLine(s.btiWave1, true) : undefined} goal={m.btiWave1.goal} before={m.btiWave1.earlier} source={src('btiWave1')} />
            ) : (
              <Tile
                label="BTI Wave 1 (2025)"
                pct={h.bti.wave1.value}
                partly={h.bti.wave1.total ? (h.bti.wave1.partly / h.bti.wave1.total) * 100 : null}
                median={h.bti.wave1.networkMedian}
                line={h.bti.wave1.value === null ? 'No results yet' : `${h.bti.wave1.inPlace} of ${h.bti.wave1.total} in place`}
                said={h.bti.wave1.value === null ? undefined : saidInPlace(h.bti.wave1.inPlace, h.bti.wave1.total, h.bti.wave1.networkMedian)}
                goal={m.btiWave1.goal}
                before={m.btiWave1.earlier}
                source={src('btiWave1')}
              />
            )}
          </div>
          </Section>
          {/* NEXT PASS 4: higher risks are their own part, so the chip above speaks only of BTI Wave 1. */}
          <Section
            half
            id="sum-frisks-h"
            question={`How many higher risks are open at ${f.firmName}?`}
            answer={h.higherRisksOpen === null ? 'Not counted yet' : `${h.higherRisksOpen} open`}
            chip={h.higherRisksOpen === null ? { label: 'After the CDW assessment', tone: 'neutral' } : h.higherRisksOpen === 0 ? { label: 'None open', tone: 'in_place' } : { label: 'No agreed response yet', tone: 'neutral' }}
            meaning={h.higherRisksOpen === null ? `Higher risks are counted once ${f.firmName}'s CDW assessment reports them.` : `Higher risks are the risks rated high or very high in ${f.firmName}'s CDW assessment that have no agreed response yet.`}
            source={src('higherRisks')}
          >
          <div className="lead-bigs">
            {h.higherRisksOpen === null ? (
              <Tile label="Higher risks open" hint={RISKS_HINT} value="Not yet" line="After CDW assessment" goal={m.higherRisks.goal} before={m.higherRisks.earlier} source={src('higherRisks')} />
            ) : (
              <Tile label="Higher risks open" hint={RISKS_HINT} value={String(h.higherRisksOpen)} line="High or very high" goal={m.higherRisks.goal} before={m.higherRisks.earlier} source={src('higherRisks')} />
            )}
          </div>
          </Section>
        </div>
      </Detail>
      <div className="lead-grid">
        <FocusSection d={d} />
      </div>
    </section>
  );
}

/** What it found (5D): the least protected business risk, then every business risk with the open findings under it. */
function WhatItFound({ rows, across, source, scope, findings, children }: {
  rows: Array<{ id: string; label: string; pct: number | null; partly: number | null; open?: number; firmsWith?: number }>;
  across: string;
  source: string;
  scope: string;
  /** The findings counted, when the reader may see them: other and unlinked ones get rows too. */
  findings?: LeadershipFinding[];
  children?: React.ReactNode;
}) {
  const by = findings ? openByBusinessRisk(findings) : null;
  const other = by?.other?.open ?? 0;
  const unlinked = by?.unlinked?.open ?? 0;
  const a = riskAnswer(rows, across);
  return (
    <Section id="found-h" question="Which business risk is least protected?" answer={a.answer} chip={a.chip} meaning={a.meaning} source={source}>
      <BusinessRiskRows rows={rows} scope={scope} unlinked={unlinked} />
      {(other > 0 || unlinked > 0) && (
        <ul className="plain lead-found-extra">
          {other > 0 && (
            <li>
              <strong>Other findings</strong> <span className="small">Outside the four business risks: {plural(other, 'open finding')}</span>
            </li>
          )}
          {unlinked > 0 && (
            <li>
              <strong>{UNSORTED_LABEL}</strong> <span className="small">{plural(unlinked, 'open finding')}; the business risk {unlinked === 1 ? 'it threatens is' : 'they threaten is'} not known yet</span>
            </li>
          )}
        </ul>
      )}
      {findings && <p className="muted small">Each finding is counted once, under the business risk most of its controls protect against.</p>}
      {familyNote(findings) && <p className="muted small lead-family-note">{familyNote(findings)}</p>}
      {showDraftLabel(LEADERSHIP_CONTENT_STATUS) && <p className="muted small">Draft: the business risk groupings are National guidance under review.</p>}
      {children && <Detail what="the findings behind each business risk">{children}</Detail>}
    </Section>
  );
}

/** A finding's or an acceptance's impact (DEC-49): the business risk it could lead to, and CDW's higher rating. */
function Impact({ riskId, linked = true, higher, unlinked = UNSORTED_LABEL }: { riskId: string | null | undefined; linked?: boolean; higher?: boolean | null; unlinked?: string }) {
  return (
    <>
      {/* NEXT PASS 5 (F4): CDW's rating is part of the impact line, so the row is only the risk, the firm and its impact. */}
      <span className="lead-impact">{impactText(linked ? riskLabel(riskId ?? null) : unlinked, higher)}</span>
    </>
  );
}

/**
 * One finding (5D, DEC-49): the risk, the firm and its impact, with its status and who says so, the repeat flag
 * (DEC-36) and the family label (DEC-40). No owner, due date or age: firms manage those on their own pages.
 */
function FindingRow({ f, showFirm }: { f: LeadershipFinding; showFirm: boolean }) {
  return (
    <li className="lead-finding">
      <p className="lead-finding-text">
        {showFirm && <span className="lead-finding-firm">{f.firmName}</span>}
        <strong>{f.text}</strong>
      </p>
      {/* DEC-55: the risk, the firm and its impact only; status, who reports it and the repeat flag stay on the
          firm's own pages. */}
      <p className="lead-finding-meta small">
        <Impact riskId={f.riskId} linked={f.linked} higher={f.higher} />
        {/* DEC-55 (NEXT PASS 2, F4): no per-row label for a finding grouped by its type; the section's note says how
            many there are (familyNote), and firms see the label on their own risk register. */}
      </p>
    </li>
  );
}

/** The open findings and what each could lead to (5D): open first, the rest one click down; closed last. */
/** NEXT PASS 5 (F1): the open findings' state in a few words: how many CDW rated high or very high. */
const higherChip = (findings: readonly LeadershipFinding[]): StateChip => higherCount(findingsInOrder(findings));
/** The one wording for "how many CDW rated high or very high" is the shared one (higherState), for findings and acceptances. */
const higherCount = higherState;

function FindingsSection({ findings, showFirm, first, firmName, state }: { findings: LeadershipFinding[]; showFirm: boolean; first?: number; firmName?: string; /** One firm's program state (PL-02), so an empty list is said the way the program card says it. */ state?: ProgramState }) {
  const sorted = findingsInOrder(findings);
  const shown = first ? sorted.slice(0, first) : sorted;
  const rest = sorted.slice(shown.length);
  const c = findingCounts(findings);
  return (
    <Section
      id="fixing-h"
      question="Which findings are open, and what could they lead to?"
      answer={findings.length ? plural(c.open, 'open finding') : 'No list yet'}
      // DEC-55 item 6 (NEXT PASS 4, F4): no status count in the header (closed findings stay on the firms' pages); only
      // the state of a list not received yet.
      chip={!findings.length ? (state ? PROGRAM_STATE_CHIP[state](null) : { label: 'Waiting for the CDW report', tone: 'neutral' }) : higherChip(findings)}
      meaning={findings.length ? <span className="lead-fixing-line">{findingsLine(c)}</span> : 'The findings list has not been received yet: it comes with the CDW risk assessment report.'}
      source={firmName ? `${firmName}'s CDW risk register, with progress as the firm reports it` : SOURCE.register}
    >
      {findings.length > 0 && (
        <>
          <ul className="plain lead-findings">
            {shown.map((f) => (
              <FindingRow key={f.id} f={f} showFirm={showFirm} />
            ))}
          </ul>
          {rest.length > 0 && (
            <details className="lead-detail">
              <summary>See the other {rest.length} findings</summary>
              <ul className="plain lead-findings">
                {rest.map((f) => (
                  <FindingRow key={f.id} f={f} showFirm={showFirm} />
                ))}
              </ul>
            </details>
          )}
          <p className="legend small">
            Impact: the business risk a finding could lead to. {UNSORTED_LABEL}: the CDW report does not name its baseline controls yet, so its business risk is not known. Each firm tracks its own findings' progress on its own pages.
          </p>
        </>
      )}
    </Section>
  );
}

/**
 * Risks accepted (5D, DEC-49, DEC-55 item 6): the risk, the firm and its impact only; no status, in the rows or in the
 * header (whether a decision still waits stays on the firm's own pages). Each firm decides its own.
 */
function RiskAcceptances({ list, showFirm, firmName }: { list: LeadershipAcceptance[]; showFirm: boolean; firmName?: string }) {
  return (
    <Section
      id="accepted-h"
      question={firmName ? `Which risks have ${firmName}'s leaders accepted?` : "Which risks have firms' leaders accepted?"}
      answer={list.length ? plural(list.length, 'risk acceptance') : 'None'}
      // NEXT PASS 5 (F1): a state, without the waiting-or-accepted status DEC-55 item 6 keeps on firms' pages.
      // 5E (DEC-57): not the count again: how many CDW rated high or very high, as the findings' chip says it.
      chip={list.length ? higherCount(list) : { label: 'Nothing recorded yet', tone: 'neutral' }}
      meaning="Risks a firm's leaders chose to live with for now; each firm decides its own."
      source={firmName ? `${firmName}'s risk acceptance records` : SOURCE.acceptances}
    >
      {list.length === 0 ? (
        <p className="small">No risks have been accepted.</p>
      ) : (
        <ul className="plain lead-findings">
          {list.map((a) => (
            <li key={a.id} className="lead-finding">
              <p className="lead-finding-text">
                {showFirm && <span className="lead-finding-firm">{a.firmName}</span>}
                <strong>{a.what}</strong>
              </p>
              <p className="lead-finding-meta small">
                {/* DEC-55: the risk, the firm and its impact only. */}
                <Impact riskId={a.riskId} linked={a.linked !== false} higher={a.higher} unlinked={ACCEPTANCE_UNLINKED_LABEL} />
              </p>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

/** The network's gaps and higher risks: what "Gaps and risks" showed (L3). */
function NetworkGaps({ n }: { n: LeadershipNetwork }) {
  return (
    <div className="lead-grid">
      <HigherRisks n={n} />
      <CommonGaps n={n} />
      <ControlsToStrengthen n={n} />
    </div>
  );
}

/** Assessment program, network (5D): where each firm is, what was found, the open findings, risks accepted. */
function NetworkAssessment({ n, canSeeNames }: { n: LeadershipNetwork; canSeeNames: boolean }) {
  const rows = (n.businessRisks ?? []).map((r) => ({ id: r.id, label: r.label, pct: r.coveragePct, partly: partlyOf(r.coveragePct, r.fullyPct), open: r.openFindings, firmsWith: r.firmsWith }));
  return (
    <section aria-labelledby="network-h" className="lead-band">
      <h2 id="network-h" className="sr-only">The network</h2>
      <OpeningCards cards={programCards({ firm: null, network: n })} note={canSeeNames ? unsortedNote(n.findings) : null} />
      <div className="lead-grid">
        <NetworkProgram n={n} />
        <WhatItFound rows={rows} across={acrossNetwork(n)} source={`${networkSourceText(n)}; ${SOURCE.register}`} scope=" at the typical firm" findings={canSeeNames ? n.findings : undefined}>
          <NetworkGaps n={n} />
        </WhatItFound>
        {canSeeNames && n.findings && <FindingsSection findings={n.findings} showFirm first={6} />}
        {canSeeNames && n.riskAcceptances && <RiskAcceptances list={n.riskAcceptances} showFirm />}
      </div>
    </section>
  );
}

/** Assessment program for one firm (5D): its program, what CDW found, the open findings, risks accepted; the network one click down. */
function FirmAssessment({ f, n, canSeeNames, member }: { f: LeadershipFirm; n: LeadershipNetwork; canSeeNames: boolean; /** The reader belongs to this firm. */ member: boolean }) {
  const list = f.findings ?? [];
  const by = openByBusinessRisk(list);
  const rows = f.businessRisks.map((r) => {
    const open = by[r.id]?.open ?? 0;
    return { id: r.id, label: r.label, pct: r.coveragePct, partly: r.coveragePct === null || !r.total ? null : (r.partly / r.total) * 100, open };
  });
  return (
    <>
    <section aria-labelledby="our-firm-h" className="lead-band">
      <h2 id="our-firm-h" className="sr-only">{f.firmName}</h2>
      <OpeningCards cards={programCards({ firm: f, network: n })} note={unsortedNote(f.findings)} />
      <div className="lead-grid">
        <FirmProgram f={f} />
        {measured(f) && (
          <WhatItFound rows={rows} across={`at ${f.firmName}`} source={`${firmSourceText(f)}; ${f.firmName}'s CDW risk register`} scope={` at ${f.firmName}`} findings={list}>
            <Section
              id="ins-h"
              name="Cyber insurance readiness"
              question={`How ready is ${f.firmName} for what insurers usually ask?`}
              // PL-02 (5E pre-gate): the answer counts in place, fully or partly, as the chip beside it does.
              answer={`${f.insurance.filter((i) => i.status === 'in_place' || i.status === 'partly').length} of ${f.insurance.length} in place (${f.insurance.filter((i) => i.status === 'in_place').length} fully)`}
              // 5E (DEC-57): the shared state words for the share in place (fully or partly), not a second count that
              // can read as a repeat of the answer's.
              chip={stateOf(f.insurance.length ? (f.insurance.filter((i) => i.status === 'in_place' || i.status === 'partly').length / f.insurance.length) * 100 : null)}
              meaning="Each item is a protection insurers usually ask about, and how far it is in place at the firm."
              source={firmSourceText(f)}
            >
              <ul className="plain lead-pills">
                {f.insurance.map((i) => (
                  <li key={i.id} className={`lead-pill tone-${COVERAGE[i.status].tone}`}>
                    <strong>{i.label}</strong>
                    <span>{COVERAGE[i.status].label}</span>
                  </li>
                ))}
              </ul>
            </Section>
            {f.nationalHelp.openGaps > 0 && (
              <p className="lead-national-help">
                National services can help with <strong>{f.nationalHelp.withService}</strong> of {f.firmName}&rsquo;s {f.nationalHelp.openGaps} controls not yet fully in place.{' '}
                {/* The firm-specific view of National services is for the firm's own members (CP-09). */}
                <Link to={member ? `/guidance/resources?firm=${encodeURIComponent(f.firmCode)}` : '/guidance/resources'}>See National services</Link>
              </p>
            )}
          </WhatItFound>
        )}
        {/* DEC-49/53 (NEXT PASS 2, F4): no "action plans due soon" here; owners, due dates and progress stay on the
            firm's own dashboard. */}
        <FindingsSection findings={list} showFirm={false} firmName={f.firmName} state={programState(f)} />
        <RiskAcceptances list={f.riskAcceptances ?? []} showFirm={false} firmName={f.firmName} />
      </div>
    </section>
    {/* 5E NEXT PASS 2 (E5): the group asks its question, at the group-title size; the region keeps its name, The network. */}
    <section aria-label="The network" className="lead-band">
      <h2 id="network-h" className="lead-group-q">How does the rest of the network stand?</h2>
      <p className="muted small">{canSeeNames ? `${PROGRAM_NAME} firm by firm, and the gaps and higher risks most firms share` : `${PROGRAM_NAME}, and the gaps and higher risks most firms share; other firms are not named`}</p>
      {/* E6 (5E NEXT PASS 2): the program by its name; the digit-walk test treats PROGRAM_NAME as a label, not a figure. */}
      <Detail what={`${PROGRAM_NAME} across the network, gaps and higher risks`}>
        <div className="lead-grid">
          <NetworkProgram n={n} />
        </div>
        <NetworkGaps n={n} />
      </Detail>
    </section>
    </>
  );
}

const STEP_STATUS: Record<ProgramStepStatus, string> = { complete: 'done', in_progress: 'under way', not_started: 'planned', tbd: 'to be confirmed' };

/** "CDW assessment started", by the start date rule (cdwStartOf), in plain words. */
export function cdwStartText(s: CdwStart): string {
  if (s.status === 'started') return s.date ? formatDate(s.date) : 'Under way, date not recorded';
  if (s.status === 'planned') return `Planned for ${s.date ? formatDate(s.date) : s.dateText}`;
  if (s.status === 'not_in_programme') return `Not in ${PROGRAM_NAME}`;
  return 'To be confirmed';
}

/**
 * The six program steps as a compact row (kickoff to roadmapping), each coloured by status, with the count
 * beside it: "4 of 6 done". The words say it all; the colours only help the eye.
 */
function StepRow({ firmName, steps }: { firmName: string; steps: ProgramStep[] }) {
  const c = stepCounts(steps);
  const label = `${firmName}: ${c.done} of ${c.total} milestones done${c.underWay ? `, ${c.underWay} under way` : ''}. ${steps.map((s) => `${PROGRAM_STEP_PLAIN[s.step]}: ${STEP_STATUS[s.status]}`).join('; ')}`;
  return (
    <span className="ms-row">
      <span className="ms-track" role="img" aria-label={label}>
        {steps.map((s) => (
          <span key={s.step} className={`ms-seg ms-${s.status}`} title={`${PROGRAM_STEP_PLAIN[s.step]}: ${STEP_STATUS[s.status]}`} />
        ))}
      </span>
      <span className="small ms-count">
        <strong>
          {c.done} of {c.total}
        </strong>{' '}
        done{c.underWay > 0 && <span className="muted">, {c.underWay} under way</span>}
      </span>
    </span>
  );
}

/** The tracker's share of the assessment complete (pct_complete), the National view's Progress figure (F4). */
function TrackerProgress({ firmName, pct: p }: { firmName: string; pct: number }) {
  return (
    <span className="tracker-progress">
      <span className="prog-track" role="img" aria-label={`${firmName}: ${Math.round(p)}% of the assessment complete, as the CDW tracker reports it`}>
        <span className="prog-fill" style={{ width: `${Math.max(0, Math.min(100, p))}%` }} />
      </span>
      <span className="small">{Math.round(p)}% complete</span>
    </span>
  );
}

const PROGRAM_BAR: Array<{ key: keyof LeadershipProgramSummary; cls: string; label: string }> = [
  { key: 'complete', cls: 'stage-complete', label: 'Assessment complete' },
  { key: 'underWay', cls: 'stage-in_progress', label: 'Assessment under way' },
  { key: 'scheduled', cls: 'stage-scheduled', label: 'Scheduled' },
  { key: 'toBeConfirmed', cls: 'stage-scheduling_tbd', label: 'Dates to be confirmed' },
  { key: 'notScheduled', cls: 'stage-not_scheduled', label: 'Not scheduled yet' },
  { key: 'notInProgramme', cls: 'stage-not_in_programme', label: `Not in ${PROGRAM_NAME}` },
];

/**
 * How the CDW assessment program is progressing across the network (Andres, 2026-09-26): one line, the firms at
 * each stage as a bar, and for National and Board every member firm with its six steps, when its CDW assessment
 * started, what comes next and its assessment dates. Firm members see the counts only.
 */
export function NetworkProgram({ n }: { n: LeadershipNetwork }) {
  const firms = n.programFirms;
  const summary: LeadershipProgramSummary = n.programSummary ?? {
    firms: n.firmsInNetwork,
    complete: n.programStages.complete ?? 0,
    underWay: n.programStages.in_progress ?? 0,
    scheduled: n.programStages.scheduled ?? 0,
    toBeConfirmed: n.programStages.scheduling_tbd ?? 0,
    notScheduled: 0,
    notInProgramme: 0,
    nextKickoffs: [],
  };
  const parts = PROGRAM_BAR.map((b) => ({ ...b, n: summary[b.key] as number })).filter((b) => b.n > 0);
  const total = parts.reduce((a, b) => a + b.n, 0) || 1;
  return (
    <Section
      id="nprog-h"
      question={`Where is each member firm in ${PROGRAM_NAME}?`}
      answer={`${summary.complete} of ${summary.firms} complete`}
      chip={summary.underWay ? { label: `${summary.underWay} under way`, tone: 'progress' } : { label: 'None under way', tone: 'neutral' }}
      // NEXT PASS 2 (F1): the meaning says what the figure is; the one-line summary sits with the bar it reads.
      meaning="Each member firm counts once, at the stage the CDW tracker records for it; complete means its CDW assessment is finished."
      source={SOURCE.tracker}
    >
      <p className="lead-program-line small">{programSummaryLine(summary)}</p>
      <div className="stage-bar" role="img" aria-label={parts.map((b) => `${b.label}: ${b.n}`).join(', ')}>
        {parts.map((b) => (
          <span key={b.key} className={`stage-seg ${b.cls}`} style={{ width: `${(b.n / total) * 100}%` }} />
        ))}
      </div>
      <ul className="plain stage-legend">
        {parts.map((b) => (
          <li key={b.key}>
            <span className={`stage-key ${b.cls}`} aria-hidden="true" />
            <strong>{b.n}</strong> {b.key === 'toBeConfirmed' ? 'with dates to be confirmed' : b.key === 'notInProgramme' ? `not in ${PROGRAM_NAME}` : b.label.replace(/^Assessment /, '').toLowerCase()}
          </li>
        ))}
      </ul>
      {firms && firms.length > 0 && (
        <>
          <div className="table-wrap" tabIndex={0} role="region" aria-label={`${PROGRAM_NAME} by firm`}>
            <table className="lead-program-table">
              <caption className="sr-only">Each member firm's stage, progress, where it is now, when its CDW assessment started and its last independent assessment</caption>
              {/* 5E (DEC-58): the widths are set here, so the progress column keeps its bars on one line. */}
              <colgroup>
                <col className="pt-firm" />
                <col className="pt-stage" />
                <col className="pt-progress" />
                <col className="pt-now" />
                <col className="pt-date" />
                <col className="pt-date" />
              </colgroup>
              <thead>
                <tr>
                  <th scope="col">Firm</th>
                  <th scope="col">Stage</th>
                  <th scope="col">Progress (CDW tracker)</th>
                  <th scope="col">Where it is now</th>
                  <th scope="col">CDW assessment started</th>
                  <th scope="col">Last independent assessment</th>
                </tr>
              </thead>
              <tbody>
                {firms.map((p) => (
                  <tr key={p.firmCode} className={p.inProgramme ? undefined : 'not-in-programme'}>
                    <th scope="row">{p.firmName}</th>
                    <td>
                      {p.stage ? (
                        <span className={`chip tone-${stageTone(p.stage)}`}>{stageLabel(p.stage)}</span>
                      ) : p.inProgramme ? (
                        <span className="chip tone-neutral">Not scheduled yet</span>
                      ) : (
                        <span className="muted">Not in {PROGRAM_NAME}</span>
                      )}
                    </td>
                    <td>
                      {/* 5D (F4): the tracker's own progress, as the National view's program table shows it, then the six steps.
                          5E (DEC-58): both bars and their labels on one line (prog-cell), at the column's set width. */}
                      <span className="prog-cell">
                        {p.pctComplete !== null && p.pctComplete !== undefined && <TrackerProgress firmName={p.firmName} pct={p.pctComplete} />}
                        {p.steps.length ? <StepRow firmName={p.firmName} steps={p.steps} /> : <span className="muted small">None yet</span>}
                      </span>
                    </td>
                    <td className="small">{plainNextStep(p.nextStep) ?? (p.inProgramme ? '' : `Not in ${PROGRAM_NAME}`)}</td>
                    <td className={p.cdwStart.status === 'started' ? undefined : 'muted'}>{cdwStartText(p.cdwStart)}</td>
                    <td>{p.lastIndependent ? formatDate(p.lastIndependent) : <span className="muted">Not yet</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="legend small ms-legend">
            Milestones, left to right: {PROGRAM_STEPS.map((s) => PROGRAM_STEP_PLAIN[s].toLowerCase()).join(', ')}.{' '}
            {(['complete', 'in_progress', 'not_started', 'tbd'] as const).map((s) => (
              <span key={s} className="ms-key">
                <span className={`ms-seg ms-${s}`} aria-hidden="true" /> {s === 'tbd' ? 'To be confirmed' : s === 'complete' ? 'Done' : s === 'in_progress' ? 'Under way' : 'Planned'}
              </span>
            ))}
            <br />
            CDW assessment started: the first dated assessment step in 2026. Kickoffs recorded in 2025 are not counted.
          </p>
          <MilestoneNames />
        </>
      )}
    </Section>
  );
}

/** The tracker's own names for the milestones, one click down from the plain ones (F1, NEXT PASS 1). */
function MilestoneNames() {
  return (
    <details className="lead-detail ms-names">
      <summary>See the detail: the CDW tracker's names for these steps</summary>
      <ul className="plain small">
        {PROGRAM_STEPS.map((s) => (
          <li key={s}>
            {PROGRAM_STEP_PLAIN[s]}: {PROGRAM_STEP_LABEL[s]}
          </li>
        ))}
      </ul>
    </details>
  );
}

const areaOf =(controlId: string) => (controlId.startsWith('E8') ? 'E8' : controlId.split('.')[0]!);

/**
 * A ranked list of controls across the firms with CDW results: each bar is the firms that rated it, the
 * counted firms first, with the National service that helps. Used for the most common gaps (not yet in place)
 * and for controls to strengthen (only partly in place).
 */
function RankedControls({ id, title, intro, items, kind, n }: {
  id: string;
  title: string;
  intro: string;
  items: Array<{ controlId: string; label: string; firms: number; firmsRated: number; names?: Array<{ firmCode: string; firmName: string }> }>;
  kind: 'not-yet' | 'partly';
  n: LeadershipNetwork;
}) {
  const what = kind === 'not-yet' ? 'not yet in place' : 'partly in place';
  const top = items[0];
  return (
    <Section
      id={id}
      name={title}
      question={kind === 'not-yet' ? 'Which controls do the most firms not have in place yet?' : 'Which controls do the most firms have only partly in place?'}
      answer={top ? top.label.replace(/\.$/, '') : 'None to show'}
      chip={top ? { label: `${what[0]!.toUpperCase()}${what.slice(1)} at ${top.firms} of ${top.firmsRated} firms`, tone: 'neutral' } : { label: 'Nothing to show yet', tone: 'neutral' }}
      meaning={intro}
      source={SOURCE.cdw}
    >
      {items.length === 0 ? (
        <p className="muted small">
          {n.firmsWithResults < 5 ? 'Shown once five or more firms have CDW results.' : kind === 'not-yet' ? 'Every firm has every rated control in place, at least partly.' : 'No control is only partly in place at any firm.'}
        </p>
      ) : (
        <>
          <ol className="plain lead-gaps">
            {items.map((g) => {
              const others = g.firmsRated - g.firms;
              const helpers = servicesForControl(g.controlId).filter((r) => isHandsOn(r.level)).map((r) => r.service.name);
              const w = (v: number) => `${g.firmsRated ? (v / g.firmsRated) * 100 : 0}%`;
              return (
                <li key={g.controlId} className="lead-gap">
                  <div className="lead-gap-text">
                    <Link to={`/guidance/controls/${encodeURIComponent(g.controlId)}`}>{g.label}</Link>
                    <span className="muted small">
                      {areaName(areaOf(g.controlId))}
                    </span>
                    {helpers.length > 0 && <span className="muted small">National can help</span>}
                  </div>
                  <div className="lead-gap-bar" role="img" aria-label={`${g.label}: ${what} at ${g.firms} of ${g.firmsRated} firms`}>
                    {g.firms > 0 && <span className={`seg ${kind}`} style={{ width: w(g.firms) }} />}
                    {others > 0 && <span className="seg in-place" style={{ width: w(others) }} />}
                  </div>
                  <span className="small lead-gap-count">
                    {what[0]!.toUpperCase() + what.slice(1)} at{' '}
                    <strong>
                      {g.firms} of {g.firmsRated}
                    </strong>{' '}
                    firms
                  </span>
                  {g.names && g.names.length > 0 && (
                    <details className="lead-gap-firms">
                      <summary className="small">
                        Which firms<span className="sr-only">: {g.label}, {what}</span>
                      </summary>
                      <ul className="plain firm-chips">
                        {g.names.map((x) => (
                          <li key={x.firmCode} className={`firm-chip ${kind}`}>
                            <span className={`gap-key ${kind}`} aria-hidden="true" />
                            {x.firmName}
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </li>
              );
            })}
          </ol>
          <p className="legend small">
            <span className={`gap-key ${kind}`} aria-hidden="true" /> {kind === 'not-yet' ? 'Not yet in place' : 'Partly in place'} <span className="gap-key in-place" aria-hidden="true" />{' '}
            Other firms
          </p>
        </>
      )}
    </Section>
  );
}

/** Most common gaps: controls the most firms do not have in place yet. Partly in place is not a gap (Andres, 2026-09-26). */
function CommonGaps({ n }: { n: LeadershipNetwork }) {
  return (
    <RankedControls
      id="gaps-h"
      title="Most common gaps"
      intro="Not yet in place at most of the firms with CDW results that rated them"
      kind="not-yet"
      n={n}
      items={n.topGaps.map((g) => ({ controlId: g.controlId, label: g.label, firms: g.firmsNotYet, firmsRated: g.firmsRated, names: g.firms }))}
    />
  );
}

/** Controls to strengthen: in place at many firms, but only partly. Maturity says how far each has to go. */
function ControlsToStrengthen({ n }: { n: LeadershipNetwork }) {
  return (
    <RankedControls
      id="strengthen-h"
      title="Controls to strengthen"
      intro="Partly in place at most firms with CDW results; finishing them raises Maturity (0 to 5)"
      kind="partly"
      n={n}
      items={(n.toStrengthen ?? []).map((g) => ({ controlId: g.controlId, label: g.label, firms: g.firmsPartly, firmsRated: g.firmsRated, names: g.firms }))}
    />
  );
}

/** Higher risks open across the network: counts for everyone, the most common risks for National and Board. */
function HigherRisks({ n }: { n: LeadershipNetwork }) {
  const r = n.higherRisks;
  return (
    <Section
      id="nrisk-h"
      name="Higher risks across the network"
      question="How many higher risks are open across the network?"
      answer={r ? `${r.open} open` : 'Not shown yet'}
      chip={r ? { label: `At ${r.firmsWith} of ${r.firmsRated} firms`, tone: 'neutral' } : { label: 'Fewer than five firms with results', tone: 'neutral' }}
      meaning="Risks rated high or very high in the CDW assessments, with no agreed response yet."
      source={SOURCE.register}
    >
      {!r ? (
        <p className="muted small">Shown once five or more firms have CDW results.</p>
      ) : (
        <>
          <div className="lead-risk-head">
            <Ring
              size={96}
              pct={r.firmsRated ? (r.firmsWith / r.firmsRated) * 100 : null}
              median={null}
              text={`${r.firmsWith}/${r.firmsRated}`}
              label={`${r.firmsWith} of ${r.firmsRated} firms have at least one higher risk open`}
              sub="firms"
              tone="count"
            />
            <p className="lead-risk-total">
              <span className="lead-big-value">{r.open}</span>
              <span>
                higher risks open, at <strong>{r.firmsWith} of {r.firmsRated}</strong> firms with a CDW risk register
              </span>
            </p>
          </div>
          {r.byFirm && r.byFirm.length > 0 && (
            <details className="lead-gap-firms">
              <summary className="small">Which firms</summary>
              <ul className="plain lead-risk-firms small">
                {r.byFirm.map((x) => (
                  <li key={x.firmCode}>
                    <span>{x.firmName}</span>
                    <span className="lead-risk-bar" aria-hidden="true">
                      <span style={{ width: `${(x.open / Math.max(...r.byFirm!.map((y) => y.open))) * 100}%` }} />
                    </span>
                    <span>{x.open === 1 ? '1 open' : `${x.open} open`}</span>
                  </li>
                ))}
              </ul>
            </details>
          )}
          {r.common && r.common.length > 0 && (
            <>
              <p className="small lead-plan-helps">Most common higher risks</p>
              <ol className="plain lead-risk-list small">
                {r.common.map((c) => (
                  <li key={c.riskCode}>
                    <span>{c.title}</span>
                    <span className="muted">{c.firms === 1 ? '1 firm' : `${c.firms} firms`}</span>
                  </li>
                ))}
              </ol>
            </>
          )}
        </>
      )}
    </Section>
  );
}


/** How a firm's figure is marked in "Where firms stand": CDW results plain, the firm's own rating or survey hatched. */
const standingMark = (s: LeadershipFirmStanding['source']) => (s === 'self' ? true : s === 'survey' ? ('survey' as const) : false);

/**
 * Where firms stand (BTC). National and Board see every active member firm by name (Andres, 2026-09-26): CDW
 * results plain, self-assessed firms marked "(self)", firms with only March 2025 survey answers marked "(2025)",
 * firms with nothing yet listed last. The median tick stays the CDW firms'. Firm members see unnamed columns.
 */
function WhereFirmsStand({ n, canSeeNames, selected }: { n: LeadershipNetwork; canSeeNames: boolean; selected: string | null }) {
  const all = canSeeNames ? n.firms : undefined;
  const values = (all ? all.map((f) => f.pct) : n.distribution.map((d) => d.pct)).filter((v): v is number => v !== null && v !== undefined);
  // F1 (NEXT PASS 1): the structured form, as the parts beside it: its name, the question, the spread, the typical firm.
  // SFH-06 (5E pre-gate): without every member firm's figure (the CDW firms only), the chart is named for what it shows
  // and says how many of the member firms those are; results held back below five firms are said as such.
  // A firm member's anonymous columns keep the one BTC chart name (N4); the count line says how many firms they are.
  const cdwOnly = !all;
  const named = cdwOnly && canSeeNames;
  return (
    <Section
      id="dots-h"
      name={named ? 'Member firms with CDW results' : BTC_CHART}
      question="How much of the BTC Baseline does each firm have in place?"
      answer={values.length ? `${pct(Math.min(...values))} to ${pct(Math.max(...values))}` : 'No results yet'}
      chip={{ label: n.btcMedian === null ? 'No typical firm yet' : `Typical firm ${pct(n.btcMedian)}`, tone: 'neutral' }}
      meaning={
        all || canSeeNames
          ? 'Share of the 56 BTC Baseline controls in place, fully or partly, highest first; the tick is the median of firms with CDW results.'
          : 'Share of the 56 BTC Baseline controls in place, fully or partly, highest first, other firms not named; the line is the median of firms with CDW results.'
      }
      source={all ? `${SOURCE.cdw}; firms' own ratings and March 2025 survey answers, marked` : SOURCE.cdw}
    >
      {all && all.length > 0 ? (
        <RankedBars
          caption="BTC Baseline"
          median={n.btcMedian}
          rows={all.map((f) => ({ key: f.firmCode, label: f.firmName, value: f.pct, partly: f.partlyPct, marked: standingMark(f.source), you: f.firmCode === selected }))}
        />
      ) : n.distribution.length === 0 ? (
        <p className="muted small">{n.firmsWithResults > 0 ? 'Shown once five or more firms have CDW results.' : 'Firm results appear here once firms have their CDW results.'}</p>
      ) : canSeeNames ? (
        <RankedBars
          caption="BTC Baseline"
          median={n.btcMedian}
          rows={n.distribution.map((d, i) => ({ key: d.firmCode ?? String(i), label: d.firmName ?? 'Firm', value: d.pct, partly: d.partlyPct ?? null, you: d.isYou }))}
        />
      ) : (
        <>
          {/* Only firms with CDW results are columns; a self-assessed firm's own figure is said apart, unranked (DEC-30). */}
          <RankedColumns caption="BTC Baseline" values={cdwDots(n).map((d) => ({ value: d.pct, partly: d.partlyPct ?? null, you: d.isYou }))} median={n.btcMedian} />
          {n.distribution.filter(apart).map((d) => (
            <p key="apart" className="small cols-apart">
              Your firm: <strong>{pct(d.pct)} in place</strong>
              {d.partlyPct ? ` (${fullyText(d.pct, d.partlyPct)})` : ''}, {apartSourceText(d)}, so not among the columns or ranked with firms that have CDW results.
            </p>
          ))}
        </>
      )}
      {cdwOnly && (
        <p className="small lead-cdw-count">
          {n.firmsWithResults} of {n.firmsInNetwork} member firms have CDW results.
        </p>
      )}
    </Section>
  );
}

/** What a network median is called, on its tiles and their spoken labels. */
const TYPICAL = 'The typical firm';

/** Points of a median only partly in place, from the in-place and fully medians (null when either is missing). */
const partlyOf = (inPlace: number | null, fully: number | null | undefined) => (inPlace === null || fully === null || fully === undefined ? null : Math.max(0, inPlace - fully));

function Network({ view, n, firm, canSeeNames, selected, asOf, member }: { view: LeadView; n: LeadershipNetwork; firm: LeadershipFirm | null; canSeeNames: boolean; selected: string | null; asOf: string; /** The reader belongs to the picked firm (a firm member's BTI tab shows only their own firm). */ member: boolean }) {
  const fm = n.fullyMedians;
  const src = (k: HeadlineMeasure) => tileSource(k, { firm: null, network: n });
  // Every network tile says its goal and earlier figure, from the one source the Summary and the PDF use (L2).
  const nm = headlineMeasures({ asOf, firm: null, network: n });
  // National and Board: the National view's firm-by-firm comparison, from the same rows (DEC-22). Firm members never get it.
  const c = canSeeNames ? n.comparison : undefined;
  return (
    <section aria-labelledby="network-h" className="lead-band">
      <h2 id="network-h" className="sr-only">The network</h2>
      {/* NEXT PASS 3 (F2): the date is a figure, so it sits in a part that says where it comes from. */}
      {n.figuresAsOf && (
        <div className="lead-grid">
          <section className="card lead-asof" aria-label="When the network figures were computed">
            <p className="muted small">Network figures as of {formatDate(n.figuresAsOf)}</p>
            <SourceLine source={`${SOURCE.cdw}, as the portal last computed them for firm members`} />
          </section>
        </div>
      )}
      {/* BTC first throughout the band (Andres, 2026-09-26): BTC Baseline and critical protections, then BTI. */}
      {/* 5D (F1): the answer cards; then what "the typical firm" means, near the top (F5). */}
      {view === 'network' && <OpeningCards cards={networkCards(n, selected, asOf)} />}
      {/* NEXT PASS 2 (F2): the note is a figure (the number of firms), so it sits in a part that says its source. */}
      {/* 5E NEXT PASS 2 (E1, E5): the note is a full-width strip in the shared structured form, and the quarterly chart
          a full-width card under it, drawn wide, so neither card is stretched to the other's height. */}
      {view === 'network' && (
        <div className="lead-grid">
          <Section
            id="typical-note-h"
            className="lead-typical-card"
            question="What does “the typical firm” mean?"
            // E5 (5E NEXT PASS 3): no median below five firms, so never "the median of 0 firms".
            // 5E NEXT PASS 4 (E5): the count is said once, in the note under it; the answer says what the median means.
            answer={n.firmsWithResults >= 5 ? 'The middle firm’s figure' : 'Shown once five or more firms have CDW results'}
            chip={n.firmsWithResults >= n.firmsInNetwork ? { label: 'Every firm counted', tone: 'in_place' } : { label: `${n.firmsInNetwork - n.firmsWithResults} still waiting for CDW`, tone: 'neutral' }}
            source={SOURCE.cdw}
          >
            <p className="lead-typical-note">{typicalFirmNote(n)}</p>
          </Section>
          <NetworkTrendCard id="ntrend-h" n={n} asOf={asOf} wide />
        </div>
      )}
      {view === 'network' && (
        <div className="lead-grid">
          <WhereFirmsStand n={n} canSeeNames={canSeeNames} selected={selected} />
          {c && (
            <>
              <BaselineByFirm c={c} which="bti" level={3} you={selected} source={SOURCE.cdw} />
              <CriticalByFirm c={c} level={3} you={selected} source={SOURCE.cdw} />
              <AreasByFirm c={c} level={3} source={SOURCE.cdw} />
              {/* 5D (F5, DEC-49): where firms are today only; plans, the March 2025 survey and last update stay on the National view. */}
              <FirmByFirmTable c={c} level={3} today source={SOURCE.cdw} />
            </>
          )}
        </div>
      )}
      {/* F1 (NEXT PASS 1): the typical firm's tiles in the structured form; the name says "The typical firm" once and
          the tiles under it do not repeat it (L1, NEXT PASS 5). Screen readers still hear it in each tile's label. */}
      {view === 'network' && (
      <Section
        id="typical-h"
        name="The typical firm"
        question="How does the typical firm compare on all six measures, against their goals?"
        answer={n.btcMedian === null ? 'Not measured yet' : `${Math.round(n.btcMedian)}% of the BTC Baseline`}
        chip={stateOf(n.btcMedian)}
        meaning="Each tile is that measure's median across the firms with CDW results, with its goal and its earlier figure."
        source={networkSourceText(n)}
      >
      <div className="lead-bigs six">
        <Tile label="BTC Baseline" pct={n.btcMedian} partly={partlyOf(n.btcMedian, fm?.btc)} said={TYPICAL} goal={nm.btc.goal} before={nm.btc.earlier} source={src('btc')} />
        <Tile label="Critical protections" hint={CRITICAL_HINT} pct={n.criticalMedianPct} partly={partlyOf(n.criticalMedianPct, fm?.critical)} said={TYPICAL} goal={nm.critical.goal} before={nm.critical.earlier} source={src('critical')} />
        <Tile label="BTI Wave 1 (2025)" pct={n.btiWave1Median} partly={partlyOf(n.btiWave1Median, fm?.btiWave1)} said={TYPICAL} goal={nm.btiWave1.goal} before={nm.btiWave1.earlier} source={src('btiWave1')} />
        <Tile label="BTI Wave 2 (2026)" pct={n.btiWave2Median} partly={partlyOf(n.btiWave2Median, fm?.btiWave2)} said={TYPICAL} goal={nm.btiWave2.goal} before={nm.btiWave2.earlier} source={src('btiWave2')} />
        <Tile label="Plans on track or complete" tone="count" pct={n.plansOnTrackPct} said={TYPICAL} goal={nm.plans.goal} before={nm.plans.earlier} source={src('plans')} />
        <Tile label="Firms with results" tone="count" pct={n.firmsInNetwork ? (n.firmsWithResults / n.firmsInNetwork) * 100 : null} text={`${n.firmsWithResults}/${n.firmsInNetwork}`} line="With CDW results" goal={nm.firms.goal} before={nm.firms.earlier} source={src('firms')} />
      </div>
      </Section>
      )}
      {/* 5D (F5, DEC-49): the quarterly chart is hidden until four quarters are stored, then shows by itself. */}

      {/* BTI (5D, F6): Wave 1 and Wave 2 in one layout, by area, each requirement opening to where firms are today. */}
      {view === 'bti' && <OpeningCards cards={btiCards({ asOf, firm, network: n })} />}
      {view === 'bti' && <BtiWaves n={n} firm={firm} member={member} />}    </section>
  );
}

/** The firm picker lists every active member firm, grouped by where its figures come from (National and Board). */
const PICKER_GROUPS: Array<{ source: LeadershipFirmStanding['source']; label: string }> = [
  { source: 'cdw', label: 'CDW results' },
  { source: 'self', label: 'Self-assessed, waiting for CDW' },
  { source: 'survey', label: 'March 2025 survey only' },
  { source: 'none', label: 'No results yet' },
];

/** Every firm the picker offers, by name; older responses without the full list fall back to the CDW firms. */
const pickable = (n: LeadershipNetwork): LeadershipFirmStanding[] =>
  [...(n.firms ?? n.distribution.filter((x) => x.firmCode).map((x) => ({ firmCode: x.firmCode!, firmName: x.firmName ?? x.firmCode!, source: 'cdw' as const, pct: x.pct, partlyPct: x.partlyPct ?? null })))].sort((a, b) =>
    a.firmName.localeCompare(b.firmName),
  );

/**
 * One tab per question a reader brings (5B, 2026-10-03; 5C, 2026-10-05: the Summary leads, and the Assessment program
 * absorbs "Gaps and risks"). The view is in the URL: /leadership?view=program&firm=DEM.
 */
export type LeadView = 'summary' | 'program' | 'network' | 'bti';
export const LEAD_VIEWS: Array<{ id: LeadView; label: string; short?: string; question: string }> = [
  { id: 'summary', label: 'Summary', question: 'Where does the network stand against its goal?' },
  // 5E (DEC-58, supersedes DEC-49's name): "Risk Assessment 2026", the year from the current cycle; the address stays
  // view=program, so old addresses still land here.
  // Shortcut: on phones (UX-05) the tab shows "Assessment 2026" so the row fits 375 px; its name stays the full one.
  // Revisit if Andres wants the full name on phones too (the row would then scroll inside itself).
  { id: 'program', label: PROGRAM_NAME, short: `Assessment ${ASSESSMENT_CYCLE_YEAR}`, question: `Where is each firm in ${PROGRAM_NAME}, what did it find, and what is still open?` },
  { id: 'network', label: 'The network', question: 'How do firms compare?' },
  { id: 'bti', label: 'BTI', question: "Where does the network stand on Baker Tilly International's requirements?" },
];
/** Addresses from before 5C keep working: "At a glance" is now the Summary, "Gaps and risks" part of the Assessment program. */
const OLD_VIEWS: Record<string, LeadView> = { glance: 'summary', gaps: 'program' };
/** The tab's question, naming the firm where the tab is about one firm. */
const questionFor = (v: LeadView, firmName: string | null): string =>
  firmName && v === 'summary'
    ? `Where does ${firmName} stand against the goal?`
    : firmName && v === 'program'
      ? `Where is ${firmName} in ${PROGRAM_NAME}, what did it find, and what is still open?`
      : firmName && v === 'bti'
        ? `Where does ${firmName} stand on Baker Tilly International's requirements?`
        : LEAD_VIEWS.find((x) => x.id === v)!.question;

export function Leadership() {
  const [params, setParams] = useSearchParams();
  const firm = params.get('firm');
  // The tab (?view=) and its tablist are the shared ones (web/src/tabs.tsx): each change is a history entry, built from
  // the live address; an address from before 5C (?view=glance or gaps) is rewritten without a history entry (CP-08).
  const { view, setView, liveView } = useViewParam(LEAD_VIEWS, 'summary', OLD_VIEWS);
  // The firm picker follows the same rule: a history entry, built from the live address, so the firm and the view
  // keep each other and two quick changes never work from an older query.
  const setFirm = (value: string) => {
    const next = new URLSearchParams(window.location.search);
    next.set('firm', value);
    if (next.toString() === new URLSearchParams(window.location.search).toString()) return;
    setParams(next);
  };
  // The layout's own read of who the reader is, not a second one (CP-07).
  const me = useMeContext();
  const state = useApi<LeadershipData>(`/leadership${firm ? `?firm=${encodeURIComponent(firm)}` : ''}`);
  const pdf = useDownloadState();
  // CP-08 (5E pre-gate): a PDF failure belongs to the firm it was asked for; picking another firm clears it.
  const clearPdfError = pdf.clear;
  useEffect(() => clearPdfError(), [firm, clearPdfError]);
  if (state.data && 'formerFirm' in state.data && (state.data as { formerFirm?: boolean }).formerFirm) {
    return (
      <>
        <PageBack to="/">Back to Home</PageBack>
        <FormerFirmPage />
      </>
    );
  }
  const network = me.data && (me.data.tiers.national || me.data.tiers.board);
  // A member of two or more firm groups picks between their own firms (CP-07); they never see the network by name.
  const ownFirms = network ? [] : (me.data?.firms ?? []);
  // The picker follows the address, so it shows the firm asked for while that firm loads, not the last one (CP-01).
  const asked = firm ? (firm.toLowerCase() === 'network' ? 'network' : firm.toUpperCase()) : null;
  // The one rule for the network firm picker; the Summary's "pick a firm" note uses it too (CP-10).
  const networkPicker = (d: LeadershipData) => Boolean(network && d.canSeeNames && pickable(d.network).length > 0);
  // While a different firm (or the network) loads, say so rather than show the last one's figures under it (CP-02).
  const shown = state.data ? (state.data.firm?.firmCode ?? 'network') : null;
  const switching = Boolean(state.loading && state.data && asked && asked !== shown);
  const askedName =
    asked === 'network'
      ? 'the network'
      : ((state.data && pickable(state.data.network).find((x) => x.firmCode === asked)?.firmName) ?? me.data?.firms.find((x) => x.firmCode === asked)?.displayName ?? asked ?? 'the view');
  return (
    <>
      <PageBack to="/">Back to Home</PageBack>
      <PageHeader title="Cyber resilience" />
      {/* Who the reader is decides the firm picker and the PDF's name; until it is known they wait, and a failure is said (CP-07). */}
      {me.error && (
        <p role="alert" className="notice">
          Your access could not be read: {me.error.message}
        </p>
      )}
      {state.error && firm ? (
        // A firm that cannot be shown (outside the caller's access, or gone) is not a dead end (CP-03).
        <div role="alert" className="notice">
          <p>{state.error.message}</p>
          <p>
            <button className="pill secondary-dark" onClick={state.reload}>
              Try again
            </button>{' '}
            <Link to={`/leadership?view=${view}`}>{network ? 'Show the network instead' : 'Show your firm instead'}</Link>
          </p>
        </div>
      ) : (
      <Load state={state} what="the leadership view">
        {(d) => (
          <>
            <div className="lead-toolbar">
              <span className="muted small">As of {formatDate(d.asOf)}</span>
              <details className="small lead-how">
                <summary>How these figures are counted</summary>
                <p>{d.measure}</p>
                <p>CDW is the independent firm carrying out the 2026 cyber assessments across the network.</p>
                {/* 5D: what CDW assesses, from the old What was assessed card, kept here one click down (DEC-49). */}
                <p>What CDW assesses at each firm: {CDW_SCOPE.assesses}. Not covered yet: {CDW_SCOPE.notCovered}.</p>
              </details>
              {ownFirms.length > 1 && (
                <label className="small">
                  Firm{' '}
                  <select value={asked ?? d.firm?.firmCode ?? ownFirms[0]!.firmCode} onChange={(e) => setFirm(e.target.value)}>
                    {ownFirms.map((f) => (
                      <option key={f.firmCode} value={f.firmCode}>
                        {f.displayName}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {networkPicker(d) && (
                <label className="small">
                  Firm{' '}
                  <select value={asked ?? d.firm?.firmCode ?? 'network'} onChange={(e) => setFirm(e.target.value)}>
                    <option value="network">Network only</option>
                    {PICKER_GROUPS.map((g) => {
                      const firms = pickable(d.network).filter((f) => f.source === g.source);
                      return firms.length ? (
                        <optgroup key={g.source} label={g.label}>
                          {firms.map((f) => (
                            <option key={f.firmCode} value={f.firmCode}>
                              {f.firmName}
                            </option>
                          ))}
                        </optgroup>
                      ) : null;
                    })}
                  </select>
                </label>
              )}
              {/* While another firm loads, the figures on screen are the previous firm's: no PDF under its name (CP-02). The
                  request itself shows its state and its failure here, in the portal's words (CP-06). */}
              <button
                className="pill"
                disabled={state.loading || pdf.busy}
                aria-busy={pdf.busy}
                onClick={() => pdf.download(`/leadership/pdf${firm ? `?firm=${encodeURIComponent(firm)}` : ''}`, `Cyber resilience leadership summary ${d.firm?.firmName ?? 'network'} ${d.asOf}.pdf`)}
              >
                {state.loading ? 'Loading…' : pdf.busy ? 'Preparing the PDF…' : 'Download one-page PDF'}
              </button>
              {pdf.error && (
                <p role="alert" className="small lead-pdf-error">
                  The PDF could not be prepared: {pdf.error.message}
                </p>
              )}
            </div>
            <ViewTabs views={LEAD_VIEWS} view={view} onSelect={setView} liveView={liveView} label="Leadership views" idPrefix="lead" />
            <div id="lead-panel" role="tabpanel" aria-labelledby={`lead-tab-${view}`} aria-busy={switching} tabIndex={0} className="lead-panel">
              {switching ? (
                <p role="status" className="lead-loading">
                  Loading {askedName}…
                </p>
              ) : (
              <>
              <div className="lead-question-row">
                <h2 className="lead-question">{questionFor(view, d.firm?.firmName ?? null)}</h2>
                {/* The key names every colour the tab uses, plum included (DEC-28): plans with a firm picked, plans or firms network only. */}
                {(view === 'summary' || view === 'network') && <InPlaceKey count={view === 'summary' && d.firm ? 'Plans counted' : true} median={view === 'summary' && Boolean(d.firm) && d.firm!.source !== null} survey={view === 'summary' && d.firm?.source === null && Boolean(d.firm?.march2025Baselines)} />}
                {view === 'program' && <InPlaceKey />}
              </div>
              {/* 5C: the Summary and the Assessment program are the picked firm's, or the network's when none is picked. */}
              {view === 'summary' && <Summary d={d} />}
              {view === 'program' &&
                (d.firm ? (
                  <FirmAssessment
                    f={d.firm}
                    n={d.network}
                    canSeeNames={d.canSeeNames}
                    member={Boolean(me.data?.firms.some((x) => x.firmCode === d.firm!.firmCode))}
                  />
                ) : (
                  <NetworkAssessment n={d.network} canSeeNames={d.canSeeNames} />
                ))}
              {(view === 'network' || view === 'bti') && <Network view={view} n={d.network} firm={d.firm} canSeeNames={d.canSeeNames} selected={d.firm?.firmCode ?? null} asOf={d.asOf} member={Boolean(d.firm && !d.canSeeNames)} />}
              </>
              )}
            </div>
          </>
        )}
      </Load>
      )}
    </>
  );
}
